import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Eye, Building2, User as UserIcon, Landmark } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { apiGet } from "@/lib/api";
import { CUSTOMER_STATUS_VARIANT, CUSTOMER_TYPE_VARIANT } from "@/lib/rbac";
import { formatAOAFull, formatRel } from "@/lib/sla";
import pt from "@/lib/i18n";

const TYPE_ICON = {
    individual: UserIcon,
    enterprise: Building2,
    government: Landmark,
};

const churnLabel = (s) => {
    if (s == null) return { label: "—", variant: "neutral" };
    if (s < 0.3) return { label: pt.customers.churnLow, variant: "success" };
    if (s < 0.6) return { label: pt.customers.churnMed, variant: "warning" };
    return { label: pt.customers.churnHigh, variant: "error" };
};

const CustomersPage = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const load = async () => {
        setRows(null);
        try {
            const params = new URLSearchParams();
            if (typeFilter) params.set("customer_type", typeFilter);
            if (statusFilter) params.set("status", statusFilter);
            if (search) params.set("search", search);
            params.set("page_size", "100");
            const data = await apiGet(`/customers?${params}`);
            setRows(data || []);
        } catch (e) {
            setRows([]);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeFilter, statusFilter]);

    useEffect(() => {
        const id = setTimeout(load, 300);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    return (
        <div data-testid="customers-root">
            <PageHeader
                title={pt.customers.title}
                subtitle={pt.customers.subtitle}
                right={
                    <NxButton
                        variant="copper"
                        icon={<Plus size={16} />}
                        onClick={() => navigate("/servicos/nova")}
                        testId="new-customer-button"
                    >
                        {pt.customers.new}
                    </NxButton>
                }
            />

            <GlassCard padding="p-4" className="mb-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <GlassInput
                        prefix={<Search size={16} />}
                        placeholder={pt.customers.searchPlaceholder || "Pesquisar por nome, telefone, BI…"}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        testId="customers-search"
                    />
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="glass-input"
                        data-testid="customers-type-filter"
                    >
                        <option value="">{pt.customers.filterType}</option>
                        {Object.entries(pt.customers.types).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="glass-input"
                        data-testid="customers-status-filter"
                    >
                        <option value="">{pt.customers.filterStatus}</option>
                        {Object.entries(pt.customers.statuses).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>
            </GlassCard>

            {rows === null ? (
                <GlassCard padding="p-6">
                    <div className="space-y-3">
                        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height="48px" />)}
                    </div>
                </GlassCard>
            ) : rows.length === 0 ? (
                <EmptyState icon={<UserIcon size={24} />} title={pt.customers.empty} body="Ajuste os filtros ou crie um novo cliente." />
            ) : (
                <GlassCard padding="p-0" testId="customers-table">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[color:var(--glass-border)]">
                                    {[pt.customers.col_name, pt.customers.col_type, pt.customers.col_phone, pt.customers.col_lifetime, pt.customers.col_risk, pt.customers.col_status, ""].map((h, i) => (
                                        <th key={i} className="text-left px-5 py-3 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c) => {
                                    const Icon = TYPE_ICON[c.customer_type] || UserIcon;
                                    const churn = churnLabel(c.churn_risk_score);
                                    return (
                                        <tr
                                            key={c.id}
                                            onClick={() => navigate(`/clientes/${c.id}`)}
                                            className="border-b border-[color:var(--glass-border-subtle)] hover:bg-[color:var(--copper-subtle)] cursor-pointer transition-colors"
                                            data-testid={`customer-row-${c.id}`}
                                        >
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-3">
                                                    <NxAvatar name={c.full_name} size="md" />
                                                    <div>
                                                        <p className="font-body text-sm text-[color:var(--slate-deep)] font-medium">{c.full_name}</p>
                                                        {c.email && (
                                                            <p className="font-mono text-[10px] text-[color:var(--slate-light)]">{c.email}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <NxBadge variant={CUSTOMER_TYPE_VARIANT[c.customer_type]}>
                                                    <Icon size={12} /> {pt.customers.types[c.customer_type]}
                                                </NxBadge>
                                            </td>
                                            <td className="px-5 py-3.5 font-mono text-xs text-[color:var(--slate-mid)]">{c.phone}</td>
                                            <td className="px-5 py-3.5 font-mono text-sm text-[color:var(--copper-dark)]">{formatAOAFull(c.lifetime_value_aoa)}</td>
                                            <td className="px-5 py-3.5">
                                                <NxBadge variant={churn.variant}>{churn.label}</NxBadge>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <NxBadge variant={CUSTOMER_STATUS_VARIANT[c.status]}>{pt.customers.statuses[c.status]}</NxBadge>
                                            </td>
                                            <td className="px-5 py-3.5"><Eye size={15} className="text-[color:var(--slate-light)]" /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </GlassCard>
            )}
        </div>
    );
};

export default CustomersPage;
